import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from "react";
import { Link, useLocation, useNavigate, type LinkProps } from "react-router-dom";
import { useConfirm } from "./ui/ConfirmDialog.js";
import { clearDraft, saveDraft } from "../lib/draftStorage.js";
import { mirrorAction, shouldFlushOnLeave } from "../lib/unsavedChanges.js";

const DRAFT_MIRROR_MS = 500;

interface UnsavedChangesApi {
  /** Called by an editor to publish its dirty state. Pass count 0 when clean. */
  register(scopeId: string, count: number): void;
  unregister(scopeId: string): void;
  /** Resolves true if it is safe to leave (clean, or the user confirmed discard). */
  confirmLeave(): Promise<boolean>;
  count: number;
}

function unusedUnsaved(): never {
  throw new Error("useUnsavedChanges must be used within UnsavedChangesProvider");
}

const UnsavedChangesContext = createContext<UnsavedChangesApi>({
  register: unusedUnsaved,
  unregister: unusedUnsaved,
  confirmLeave: unusedUnsaved,
  count: 0,
});

/** App-wide dirty-state registry, in-app leave-guard, and beforeunload handler.
 *  Wraps the router so <GuardedLink> and useGuardedNavigate can prompt before
 *  navigating away from an editor with unsaved edits. */
export function UnsavedChangesProvider({ children }: { children: ReactNode }) {
  const [counts, setCounts] = useState<Record<string, number>>({});
  const count = useMemo(
    () => Object.values(counts).reduce((sum, value) => sum + value, 0),
    [counts]
  );
  const countRef = useRef(count);
  countRef.current = count;
  const confirm = useConfirm();

  const register = useCallback((scopeId: string, nextCount: number) => {
    setCounts((prev) => (prev[scopeId] === nextCount ? prev : { ...prev, [scopeId]: nextCount }));
  }, []);

  const unregister = useCallback((scopeId: string) => {
    setCounts((prev) => {
      if (!(scopeId in prev)) return prev;
      const next = { ...prev };
      delete next[scopeId];
      return next;
    });
  }, []);

  const confirmLeave = useCallback(async () => {
    const current = countRef.current;
    if (current === 0) return true;
    return confirm({
      title: "Leave this page?",
      message: `You have ${current} unsaved change${current === 1 ? "" : "s"}. Leave this page and lose ${
        current === 1 ? "it" : "them"
      }?`,
      confirmLabel: "Leave",
      destructive: true,
    });
  }, [confirm]);

  useEffect(() => {
    if (count === 0) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [count]);

  const api = useMemo<UnsavedChangesApi>(
    () => ({ register, unregister, confirmLeave, count }),
    [register, unregister, confirmLeave, count]
  );

  return <UnsavedChangesContext.Provider value={api}>{children}</UnsavedChangesContext.Provider>;
}

export function useUnsavedChanges(): UnsavedChangesApi {
  return useContext(UnsavedChangesContext);
}

/** Convenience for an editor: registers on mount / count-change, unregisters on unmount. */
export function useRegisterUnsaved(scopeId: string, count: number): void {
  const { register, unregister } = useUnsavedChanges();
  useEffect(() => {
    register(scopeId, count);
  }, [scopeId, count, register]);
  useEffect(() => () => unregister(scopeId), [scopeId, unregister]);
}

/** Mirror dirty form state to sessionStorage, debounced, flushing on unmount.
 *  When the draft returns to clean - the user undid their own edit - the mirror
 *  this visit wrote is removed, so the next mount does not offer to restore a
 *  no-op change. A mirror written by an *earlier* visit is left alone: a restore
 *  offer the user navigated past without acting on must survive. */
export function useDraftMirror(
  key: string,
  draft: unknown,
  enabled: boolean
): { discard: () => void } {
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** True once this visit has written a mirror for `key`. Gates the clear. */
  const hasMirroredRef = useRef(false);
  /** True after a successful save until `key` changes. Suppresses the leave flush (NT-005). */
  const discardedRef = useRef(false);

  // Declared first so a `key` change resets the record before the mirror effect runs.
  // The reset body runs after the previous key's cleanups, so those still see discarded=true.
  useEffect(() => {
    hasMirroredRef.current = false;
    discardedRef.current = false;
  }, [key]);

  useEffect(() => {
    if (discardedRef.current) return;
    const action = mirrorAction(enabled, hasMirroredRef.current);
    if (action !== "write") {
      if (action === "clear") {
        hasMirroredRef.current = false;
        clearDraft(key);
      }
      return;
    }
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      hasMirroredRef.current = true;
      saveDraft(key, draftRef.current);
    }, DRAFT_MIRROR_MS);
    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [key, draft, enabled]);

  useEffect(
    () => () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      if (shouldFlushOnLeave(enabledRef.current, discardedRef.current)) {
        hasMirroredRef.current = true;
        saveDraft(key, draftRef.current);
      }
    },
    [key]
  );

  const discard = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    hasMirroredRef.current = false;
    discardedRef.current = true;
    clearDraft(key);
  }, [key]);

  return { discard };
}

function destinationPath(to: LinkProps["to"]): string | undefined {
  if (typeof to === "string") return to.split("?")[0];
  if (to && typeof to === "object" && "pathname" in to && typeof to.pathname === "string") {
    return to.pathname;
  }
  return undefined;
}

/** <Link> that prompts before in-app navigation when the current page is dirty.
 *  Middle-click / modifier-click keep the real href and skip the prompt. */
export function GuardedLink({ onClick, to, ...props }: LinkProps) {
  const { confirmLeave } = useUnsavedChanges();
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <Link
      {...props}
      to={to}
      onClick={(event) => {
        onClick?.(event);
        if (event.defaultPrevented) return;
        if (event.button !== 0) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const dest = destinationPath(to);
        if (dest && dest === location.pathname) return;
        event.preventDefault();
        void confirmLeave().then((ok) => {
          if (ok) navigate(to);
        });
      }}
    />
  );
}

/** For links rendered outside an editor (app nav, account menu): returns a
 *  factory that builds an <a> onClick handler which prompts before leaving a
 *  dirty page, then navigates. A no-op when nothing is dirty. */
export function useGuardedNavigate(): (
  to: string
) => (event: ReactMouseEvent<HTMLAnchorElement>) => void {
  const { confirmLeave } = useUnsavedChanges();
  const navigate = useNavigate();
  const location = useLocation();

  return useCallback(
    (to: string) => (event: ReactMouseEvent<HTMLAnchorElement>) => {
      if (event.defaultPrevented) return;
      if (event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (to.split("?")[0] === location.pathname) return;
      event.preventDefault();
      void confirmLeave().then((ok) => {
        if (ok) navigate(to);
      });
    },
    [confirmLeave, navigate, location.pathname]
  );
}
